import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Stamp as StampIcon, BookOpen, Sparkles } from "lucide-react";
import Footer from "../components/Footer";

export default function Passport() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [allCities, setAllCities] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([api.get("/passport"), api.get("/cities")])
      .then(([p, c]) => {
        setData(p.data);
        setAllCities(c.data);
      })
      .catch((e) => {
        // Don't strand the page on "Loading passport…" if either call fails.
        // Render an empty passport — ProtectedRoute will already have redirected
        // on real auth failures by the time we land here.
        setData({ stamps: [], xp: 0, level: 1 });
        setAllCities([]);
        if (e?.response?.status && e.response.status !== 401) {
          setErr("Couldn't load your passport just now — pull to refresh.");
        }
      });
  }, []);

  if (!data) return <div className="pt-32 text-center text-cream-dim font-display tracking-widest">Loading passport…</div>;

  // ErrorBanner above the book if a non-auth fetch failed
  const errorBanner = err ? (
    <div className="iq-card border-rose-500/40 bg-rose-500/10 text-rose-200 p-4 mb-6 text-sm">{err}</div>
  ) : null;

  const collectedSlugs = new Set(data.stamps.map((s) => s.slug));
  const progress = allCities.length ? Math.round((data.stamps.length / allCities.length) * 100) : 0;

  return (
    <div data-testid="passport-page" className="pt-safe-navbar-lg pb-16">
      <div className="max-w-[1100px] mx-auto px-6">
        <div className="flex items-start justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="iq-overline mb-2 flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> InterQuest Passport</div>
            <h1 className="font-display text-cream text-5xl md:text-6xl tracking-wider">{user.name}</h1>
            <p className="font-serif italic text-cream-dim text-lg mt-1">
              "Every journey leaves its mark."
            </p>
          </div>
          <div className="iq-card p-5 min-w-[240px]">
            <div className="iq-overline mb-2">Statistics</div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="font-display text-3xl text-gold">{data.stamps.length}</div>
                <div className="text-[10px] text-cream-dim uppercase tracking-widest mt-1">Stamps</div>
              </div>
              <div>
                <div className="font-display text-3xl text-cream">LVL {data.level}</div>
                <div className="text-[10px] text-cream-dim uppercase tracking-widest mt-1">Rank</div>
              </div>
              <div>
                <div className="font-display text-3xl text-cream">{data.xp}</div>
                <div className="text-[10px] text-cream-dim uppercase tracking-widest mt-1">XP</div>
              </div>
            </div>
            <div className="mt-4 h-1.5 bg-panel-border rounded-full overflow-hidden">
              <div className="h-full bg-gold" style={{ width: `${progress}%` }} />
            </div>
            <div className="mt-1.5 text-[10px] text-cream-dim/70 text-center">{progress}% of curated cities collected</div>
          </div>
        </div>

        {errorBanner}

        {/* Passport book spread */}
        <div className="iq-card p-8 md:p-12 relative" data-testid="passport-book">
          <div className="absolute top-0 bottom-0 left-1/2 w-px bg-panel-border hidden md:block" />
          <div className="iq-overline mb-6 text-center">
            <Sparkles className="inline h-3 w-3 mr-1" />
            COLLECTED STAMPS
          </div>

          {data.stamps.length === 0 ? (
            <div className="text-center py-16">
              <StampIcon className="h-12 w-12 text-gold/40 mx-auto mb-4" />
              <p className="font-serif italic text-cream-dim text-lg">
                Empty pages, full of promise. Save your first route to earn a stamp.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
              {data.stamps.map((s, i) => (
                <div
                  key={s.slug}
                  data-testid={`stamp-${s.slug}`}
                  className="iq-stamp rounded-sm p-4 text-center animate-stamp-in flex flex-col items-center justify-center aspect-square"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <div className="text-[10px] tracking-[0.25em] uppercase opacity-70 mb-1">{s.country}</div>
                  <div className="font-display text-2xl tracking-wider">{s.name}</div>
                  <div className="mt-2 text-[10px] opacity-60 tracking-widest uppercase">VISITED</div>
                  <div className="mt-1 text-[10px] font-serif italic opacity-50">{s.collected_at ? new Date(s.collected_at).getFullYear() : new Date().getFullYear()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Uncollected cities preview */}
        {data.stamps.length < allCities.length && (
          <div className="mt-12">
            <div className="iq-overline mb-4">Cities yet to visit</div>
            <div className="flex flex-wrap gap-2">
              {allCities.filter((c) => !collectedSlugs.has(c.slug)).map((c) => (
                <span
                  key={c.slug}
                  className="text-xs px-3 py-1.5 rounded-sm border border-dashed border-panel-border text-cream-dim/60"
                >
                  {c.name} · {c.country}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
